/**
 * DataTable.
 *
 * Lightweight, headers-and-rows table matching the UX spec:
 *   - Row height 36px, padding px-3 py-2
 *   - Column headers: caption-strong, uppercase, text-secondary
 *   - Sticky header on scroll
 *   - Row hover: bg-subtle
 *   - Empty cells: en-dash (handled by formatters; this just renders ReactNode)
 *
 * Type parameter `T` is the row type. Columns declare an `accessor` (key of T)
 * or a `cell` render function. Right-aligned columns get tabular-nums.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface ColumnDef<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-align the cell + apply tabular-nums. Use for numbers, dates. */
  numeric?: boolean;
  /** Restrict width on wider screens; the cell's content can ellipsize. */
  width?: string;
}

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  className?: string;
  /** Sticky header within a scroll container. Default true. */
  stickyHeader?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  className,
  stickyHeader = true,
}: DataTableProps<T>) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full border-collapse text-sm">
        <thead className={cn(stickyHeader ? 'sticky top-0 z-10 bg-surface' : '')}>
          <tr className="border-b border-border-default">
            {columns.map((col) => (
              <th
                key={col.id}
                className={cn(
                  'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-secondary',
                  col.numeric ? 'text-right' : 'text-left',
                )}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = rowKey(row);
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-border-default last:border-b-0',
                  // Patch P21 — back to neutral hover. Tinting every table
                  // row magenta on hover was over-application of the brand
                  // colour; accent stays for commands (buttons + active
                  // states inside cells), not for ambient interaction
                  // feedback.
                  onRowClick ? 'cursor-pointer hover:bg-subtle' : '',
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.id}
                    className={cn(
                      'px-3 py-2 align-middle text-sm',
                      col.numeric ? 'text-right tabular-nums' : '',
                    )}
                  >
                    {col.cell(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
